import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ChevronDown,
  ChevronRight,
  CloudUpload,
  Download,
  FileClock,
  FileDown,
  FileUp,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  HardDrive,
  Image as ImageIcon,
  Lock,
  MoreHorizontal,
  Pencil,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { useAuth } from '@/app/AuthProvider';
import {
  FOLDER_INPUT_PROPS,
  pickedFromDataTransfer,
  pickedFromFileList,
  type PickedFile,
} from '@/lib/fileTree';
import {
  useCreateEngFolder,
  useDeleteEngFile,
  useDeleteEngFolder,
  useDownloadEngFile,
  useEngFiles,
  useEngFolders,
  useUpdateEngFile,
  useUpdateEngFolder,
  useUpdateEngProject,
  type EngFile,
  type EngFolder,
  type EngProject,
} from '@/features/engineering/hooks';
import { formatBytes, isImageFile, isViewable3D } from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { FileIcon, PersonChip } from './EngParts';
import {
  buildFolderTree,
  countFilesInFolder,
  flattenTree,
  folderPath,
  sortFiles,
  type FolderNode,
} from './engFileUtils';
import { EngNewVersionDialog, EngUploadDialog, type PendingUpload } from './EngUploadDialogs';
import { EngFileSheet } from './EngFileSheet';
import { ModelViewerDialog, type ModelViewerTarget } from './ModelViewerDialog';

interface EngFilesTabProps {
  project: EngProject;
  canEdit: boolean;
  canManage: boolean;
  locked: boolean;
}

type NameEdit =
  | { kind: 'new-folder'; parentId: string | null }
  | { kind: 'rename-folder'; folder: EngFolder }
  | { kind: 'rename-file'; file: EngFile };

/**
 * The project's file drive: folders, files with version counts and
 * check-out state, upload (files, whole folders, drag-and-drop), the
 * version-history sheet and the 3D viewer. Everything that writes is
 * hidden while the project is in a locked status; downloads and the
 * viewer keep working.
 */
export function EngFilesTab({ project, canEdit, canManage, locked }: EngFilesTabProps) {
  const { profile } = useAuth();
  const { data: folders, isLoading: foldersLoading } = useEngFolders(project.id);
  const { data: files, isLoading: filesLoading } = useEngFiles(project.id);
  const createFolder = useCreateEngFolder();
  const updateFolder = useUpdateEngFolder();
  const deleteFolder = useDeleteEngFolder();
  const updateFile = useUpdateEngFile();
  const deleteFile = useDeleteEngFile();
  const download = useDownloadEngFile();
  const updateProject = useUpdateEngProject();

  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [nameEdit, setNameEdit] = useState<NameEdit | null>(null);
  const [openFileId, setOpenFileId] = useState<string | null>(null);
  const [versionFor, setVersionFor] = useState<EngFile | null>(null);
  const [preview, setPreview] = useState<ModelViewerTarget | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const folderList = useMemo(() => folders ?? [], [folders]);
  const fileList = useMemo(() => files ?? [], [files]);
  const tree = useMemo(() => buildFolderTree(folderList), [folderList]);
  const flatFolders = useMemo(() => flattenTree(tree), [tree]);
  const editable = canEdit && !locked;

  // If the current folder disappears (deleted elsewhere), fall back to root.
  useEffect(() => {
    if (currentFolderId && !folderList.some((f) => f.id === currentFolderId)) setCurrentFolderId(null);
  }, [currentFolderId, folderList]);

  const openFile = openFileId ? fileList.find((f) => f.id === openFileId) ?? null : null;

  const searching = query.trim().length > 0;
  const visibleFiles = useMemo(() => {
    if (searching) {
      const q = query.trim().toLowerCase();
      return sortFiles(
        fileList.filter(
          (f) => f.name.toLowerCase().includes(q) || (f.description ?? '').toLowerCase().includes(q),
        ),
      );
    }
    return sortFiles(fileList.filter((f) => (f.folder_id ?? null) === currentFolderId));
  }, [fileList, currentFolderId, query, searching]);

  const childFolders = useMemo(
    () => (searching ? [] : flatFolders.filter((n) => (n.folder.parent_id ?? null) === currentFolderId)),
    [flatFolders, currentFolderId, searching],
  );

  const crumbs = folderPath(currentFolderId, folderList);

  // ----- uploads --------------------------------------------------------

  const queueUpload = (entries: PickedFile[]) => {
    if (!editable) return;
    if (entries.length === 0) {
      toast.info('Nothing to upload.');
      return;
    }
    setPendingUpload({ folderId: currentFolderId, entries });
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (!editable) return;
    const promise = pickedFromDataTransfer(event.dataTransfer);
    void promise.then(queueUpload).catch((err) => toast.error(formatError(err, 'Could not read the dropped files.')));
  };

  // ----- folder ops -----------------------------------------------------

  const submitName = async (name: string) => {
    if (!nameEdit) return;
    try {
      if (nameEdit.kind === 'new-folder') {
        const folder = await createFolder.mutateAsync({ projectId: project.id, parentId: nameEdit.parentId, name });
        setCurrentFolderId(folder.id);
        if (nameEdit.parentId) expandChain(nameEdit.parentId);
      } else if (nameEdit.kind === 'rename-folder') {
        await updateFolder.mutateAsync({ id: nameEdit.folder.id, projectId: project.id, patch: { name } });
      } else {
        await updateFile.mutateAsync({ id: nameEdit.file.id, projectId: project.id, patch: { name } });
      }
      setNameEdit(null);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const expandChain = (folderId: string) => {
    const chain = folderPath(folderId, folderList).map((f) => f.id);
    setCollapsed((prev) => {
      const next = new Set(prev);
      for (const id of chain) next.delete(id);
      return next;
    });
  };

  const handleDeleteFolder = (folder: EngFolder) => {
    const count = countFilesInFolder(folder.id, fileList, folderList);
    const ok = window.confirm(
      `Delete folder “${folder.name}”? ${count > 0 ? `Its ${count} file${count === 1 ? '' : 's'} and any sub-folders move up one level.` : 'Sub-folders move up one level.'}`,
    );
    if (!ok) return;
    deleteFolder.mutate(
      { folder },
      {
        onSuccess: () => {
          if (currentFolderId === folder.id) setCurrentFolderId(folder.parent_id);
        },
        onError: (err) => toast.error(formatError(err)),
      },
    );
  };

  const handleDeleteFile = (file: EngFile) => {
    const ok = window.confirm(
      `Delete “${file.name}” and all ${file.version_count} version${file.version_count === 1 ? '' : 's'}? This cannot be undone.`,
    );
    if (!ok) return;
    deleteFile.mutate(
      { file },
      {
        onSuccess: () => {
          if (openFileId === file.id) setOpenFileId(null);
          toast.success('File deleted.');
        },
        onError: (err) => toast.error(formatError(err)),
      },
    );
  };

  const moveFile = (file: EngFile, folderId: string | null) => {
    updateFile.mutate(
      { id: file.id, projectId: project.id, patch: { folder_id: folderId } },
      { onError: (err) => toast.error(formatError(err)) },
    );
  };

  const setCover = (file: EngFile) => {
    if (!file.current_version) return;
    updateProject.mutate(
      { id: project.id, patch: { image_path: file.current_version.storage_path } },
      {
        onSuccess: () => toast.success('Cover image updated.'),
        onError: (err) => toast.error(formatError(err)),
      },
    );
  };

  const previewFile = (file: EngFile) => {
    if (!file.current_version) return;
    setPreview({
      storagePath: file.current_version.storage_path,
      fileName: file.current_version.file_name,
      title: `${file.name} · v${file.version_count}`,
      subtitle: file.description ?? undefined,
    });
  };

  const isLoading = foldersLoading || filesLoading;

  return (
    <div className="space-y-4">
      {locked && (
        <div className="flex items-start gap-3 rounded-lg border border-bright-moss/50 bg-bright-moss/15 px-4 py-3 text-sm text-bright-moss-dark">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Design locked — files are read-only.</p>
            <p className="text-xs opacity-90">
              Download and preview as usual. To change anything, move the project back to an open status from the header.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all files in this project…"
            className="pl-9"
            aria-label="Search files"
          />
        </div>
        {editable && (
          <div className="flex flex-wrap items-center gap-2">
            {/* `input.files` is a live FileList that resetting `value`
                empties in place, so snapshot it before the reset. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="sr-only"
              tabIndex={-1}
              onChange={(e) => {
                const picked = pickedFromFileList(e.target.files ?? []);
                e.target.value = '';
                queueUpload(picked);
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              multiple
              className="sr-only"
              tabIndex={-1}
              {...FOLDER_INPUT_PROPS}
              onChange={(e) => {
                const picked = pickedFromFileList(e.target.files ?? []);
                e.target.value = '';
                queueUpload(picked);
              }}
            />
            <Button variant="outline" size="sm" onClick={() => setNameEdit({ kind: 'new-folder', parentId: currentFolderId })}>
              <FolderPlus className="h-3.5 w-3.5" />
              New folder
            </Button>
            <Button variant="outline" size="sm" onClick={() => folderInputRef.current?.click()}>
              <FolderUp className="h-3.5 w-3.5" />
              Upload folder
            </Button>
            <Button size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" />
              Upload files
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        {/* Folder tree */}
        <Card className="h-fit">
          <CardContent className="p-2">
            <button
              type="button"
              onClick={() => {
                setCurrentFolderId(null);
                setQuery('');
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                currentFolderId === null && !searching
                  ? 'bg-info-soft/30 font-medium text-info'
                  : 'text-foreground hover:bg-secondary/60',
              )}
            >
              <HardDrive className="h-4 w-4 shrink-0" strokeWidth={1.6} />
              <span className="flex-1 truncate">{project.code} drive</span>
              <span className="text-xs tabular-nums text-muted-foreground">{fileList.length}</span>
            </button>
            {isLoading ? (
              <div className="space-y-1 p-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-5/6" />
              </div>
            ) : tree.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                No folders yet.{editable && ' Create one or upload a folder from your computer.'}
              </p>
            ) : (
              <div className="mt-1 space-y-0.5">
                {tree.map((node) => (
                  <FolderTreeRow
                    key={node.folder.id}
                    node={node}
                    files={fileList}
                    folders={folderList}
                    currentFolderId={searching ? null : currentFolderId}
                    collapsed={collapsed}
                    editable={editable}
                    onSelect={(id) => {
                      setCurrentFolderId(id);
                      setQuery('');
                    }}
                    onToggle={(id) =>
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        return next;
                      })
                    }
                    onNewSubfolder={(parentId) => setNameEdit({ kind: 'new-folder', parentId })}
                    onRename={(folder) => setNameEdit({ kind: 'rename-folder', folder })}
                    onDelete={handleDeleteFolder}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* File list */}
        <Card
          className={cn('relative min-h-[320px]', dragOver && 'ring-2 ring-info ring-offset-2')}
          onDragEnter={(e) => {
            if (!editable) return;
            e.preventDefault();
            dragDepth.current += 1;
            setDragOver(true);
          }}
          onDragOver={(e) => {
            if (!editable) return;
            e.preventDefault();
          }}
          onDragLeave={() => {
            if (!editable) return;
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0) setDragOver(false);
          }}
          onDrop={onDrop}
        >
          {dragOver && (
            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-info-soft/40 text-info backdrop-blur-[1px]">
              <CloudUpload className="h-8 w-8" />
              <p className="text-sm font-medium">Drop to upload into {crumbs.length ? crumbs[crumbs.length - 1].name : 'the drive root'}</p>
              <p className="text-xs">Folders keep their structure; matching names become new versions</p>
            </div>
          )}
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center gap-1 border-b border-border px-4 py-2.5 text-sm">
              {searching ? (
                <span className="text-muted-foreground">
                  {visibleFiles.length} result{visibleFiles.length === 1 ? '' : 's'} for “{query.trim()}”
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setCurrentFolderId(null)}
                    className={cn('rounded px-1 hover:underline', crumbs.length === 0 && 'font-medium')}
                  >
                    {project.code}
                  </button>
                  {crumbs.map((f, i) => (
                    <span key={f.id} className="flex items-center gap-1">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <button
                        type="button"
                        onClick={() => setCurrentFolderId(f.id)}
                        className={cn('rounded px-1 hover:underline', i === crumbs.length - 1 && 'font-medium')}
                      >
                        {f.name}
                      </button>
                    </span>
                  ))}
                </>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {visibleFiles.length} file{visibleFiles.length === 1 ? '' : 's'}
                {childFolders.length > 0 && ` · ${childFolders.length} folder${childFolders.length === 1 ? '' : 's'}`}
              </span>
            </div>

            {isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : childFolders.length === 0 && visibleFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-info-soft/30 text-info">
                  {searching ? <Search className="h-5 w-5" /> : <FolderOpen className="h-5 w-5" />}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {searching ? 'No files match' : 'This folder is empty'}
                  </p>
                  <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                    {searching
                      ? 'Try a different name, or clear the search to browse folders.'
                      : editable
                        ? 'Drag files or folders here, or use the upload buttons above. CAD, meshes, drawings, photos, spreadsheets — anything goes.'
                        : 'Nothing has been uploaded here yet.'}
                  </p>
                </div>
                {editable && !searching && (
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-3.5 w-3.5" />
                    Upload files
                  </Button>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {childFolders.map((node) => {
                  const count = countFilesInFolder(node.folder.id, fileList, folderList);
                  return (
                    <li key={node.folder.id}>
                      <button
                        type="button"
                        onDoubleClick={() => setCurrentFolderId(node.folder.id)}
                        onClick={() => setCurrentFolderId(node.folder.id)}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-secondary/50"
                      >
                        <Folder className="h-4 w-4 shrink-0 text-info" strokeWidth={1.6} />
                        <span className="min-w-0 flex-1 truncate font-medium">{node.folder.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {count} file{count === 1 ? '' : 's'}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </li>
                  );
                })}
                {visibleFiles.map((file) => (
                  <FileRow
                    key={file.id}
                    file={file}
                    folders={folderList}
                    flatFolders={flatFolders}
                    showPath={searching}
                    editable={editable}
                    canManage={canManage}
                    isMe={file.checked_out_by === profile?.id}
                    isCover={!!file.current_version && project.image_path === file.current_version.storage_path}
                    onOpen={() => setOpenFileId(file.id)}
                    onPreview={() => previewFile(file)}
                    onDownload={() =>
                      download.mutate({ file, checkOut: false }, { onError: (err) => toast.error(formatError(err)) })
                    }
                    onCheckOut={() =>
                      download.mutate(
                        { file, checkOut: true },
                        {
                          onSuccess: () => toast.success('Downloaded and checked out. Upload it back when you’re done.'),
                          onError: (err) => toast.error(formatError(err)),
                        },
                      )
                    }
                    onNewVersion={() => setVersionFor(file)}
                    onRename={() => setNameEdit({ kind: 'rename-file', file })}
                    onMove={(folderId) => moveFile(file, folderId)}
                    onSetCover={() => setCover(file)}
                    onDelete={() => handleDeleteFile(file)}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <EngUploadDialog
        pending={pendingUpload}
        onClose={() => setPendingUpload(null)}
        projectId={project.id}
        files={fileList}
        folders={folderList}
      />
      <EngNewVersionDialog
        file={versionFor}
        onClose={() => setVersionFor(null)}
        checkedOutByMe={!!versionFor?.checked_out_by && versionFor.checked_out_by === profile?.id}
      />
      <NameDialog
        edit={nameEdit}
        onClose={() => setNameEdit(null)}
        onSubmit={submitName}
        busy={createFolder.isPending || updateFolder.isPending || updateFile.isPending}
      />
      <EngFileSheet
        file={openFile}
        folders={folderList}
        onClose={() => setOpenFileId(null)}
        canEdit={canEdit}
        locked={locked}
        canManage={canManage}
        onPreview={setPreview}
        onNewVersion={(f) => setVersionFor(f)}
      />
      <ModelViewerDialog target={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Folder tree row
// ---------------------------------------------------------------------------

function FolderTreeRow(props: {
  node: FolderNode;
  files: EngFile[];
  folders: EngFolder[];
  currentFolderId: string | null;
  collapsed: Set<string>;
  editable: boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onNewSubfolder: (parentId: string) => void;
  onRename: (folder: EngFolder) => void;
  onDelete: (folder: EngFolder) => void;
}) {
  const { node } = props;
  const folder = node.folder;
  const isCollapsed = props.collapsed.has(folder.id);
  const active = props.currentFolderId === folder.id;
  const hasChildren = node.children.length > 0;
  const count = countFilesInFolder(folder.id, props.files, props.folders);
  const Icon = active || (!isCollapsed && hasChildren) ? FolderOpen : Folder;

  return (
    <div>
      <div
        className={cn(
          'group/row flex items-center gap-1 rounded-md pr-1 text-sm transition-colors',
          active ? 'bg-info-soft/30 text-info' : 'hover:bg-secondary/60',
        )}
        style={{ paddingLeft: 4 + node.depth * 12 }}
      >
        <button
          type="button"
          onClick={() => props.onToggle(folder.id)}
          className={cn('inline-flex h-6 w-5 shrink-0 items-center justify-center rounded text-muted-foreground', !hasChildren && 'invisible')}
          aria-label={isCollapsed ? 'Expand folder' : 'Collapse folder'}
        >
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isCollapsed && '-rotate-90')} />
        </button>
        <button
          type="button"
          onClick={() => props.onSelect(folder.id)}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
        >
          <Icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
          <span className={cn('truncate', active && 'font-medium')}>{folder.name}</span>
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">{count}</span>
        </button>
        {props.editable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary group-hover/row:opacity-100 data-[state=open]:opacity-100"
                aria-label={`Folder actions for ${folder.name}`}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right" className="text-xs">
              <DropdownMenuItem onSelect={() => props.onNewSubfolder(folder.id)}>
                <FolderPlus className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                New sub-folder
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => props.onRename(folder)}>
                <Pencil className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => props.onDelete(folder)}>
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {!isCollapsed &&
        node.children.map((child) => <FolderTreeRow key={child.folder.id} {...props} node={child} />)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// File row
// ---------------------------------------------------------------------------

function FileRow(props: {
  file: EngFile;
  folders: EngFolder[];
  flatFolders: FolderNode[];
  showPath: boolean;
  editable: boolean;
  canManage: boolean;
  isMe: boolean;
  isCover: boolean;
  onOpen: () => void;
  onPreview: () => void;
  onDownload: () => void;
  onCheckOut: () => void;
  onNewVersion: () => void;
  onRename: () => void;
  onMove: (folderId: string | null) => void;
  onSetCover: () => void;
  onDelete: () => void;
}) {
  const { file } = props;
  const viewable = isViewable3D(file.name) && !!file.current_version;
  const checkedOut = !!file.checked_out_by;
  const path = props.showPath ? folderPath(file.folder_id, props.folders).map((f) => f.name).join(' / ') : null;

  return (
    <li
      className="group/file flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary/50"
      onClick={props.onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') props.onOpen();
      }}
      role="button"
      tabIndex={0}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary text-muted-foreground">
        <FileIcon category={file.category} className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-foreground">{file.name}</span>
          <Badge variant="outline" className="shrink-0 font-mono text-[10px]">
            v{file.version_count}
          </Badge>
          {props.isCover && (
            <Badge variant="info-soft" className="shrink-0 text-[10px]">
              Cover
            </Badge>
          )}
          {checkedOut && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]',
                    props.isMe
                      ? 'border-info-soft bg-info-soft/30 text-info'
                      : 'border-fresh-salmon/50 bg-fresh-salmon/15 text-fresh-salmon-dark',
                  )}
                >
                  <FileClock className="h-3 w-3" />
                  {props.isMe ? 'You have it' : 'Checked out'}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Checked out by {file.checked_out_profile?.full_name ?? file.checked_out_profile?.email ?? 'someone'}{' '}
                {formatRelative(file.checked_out_at)}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          {path !== null && <span className="truncate">{path || 'Drive root'} ·</span>}
          <span>{formatBytes(file.current_version?.size_bytes)}</span>
          <span>· updated {formatRelative(file.current_version?.created_at ?? file.updated_at)}</span>
          {file.current_version?.change_summary && (
            <span className="hidden truncate sm:inline">· {file.current_version.change_summary}</span>
          )}
          {file.item && (
            <span className="hidden sm:inline">
              · <span className="font-mono">{file.item.sku}</span>
            </span>
          )}
        </div>
      </div>
      {checkedOut && !props.isMe && (
        <PersonChip person={file.checked_out_profile} size="xs" className="hidden md:inline-flex [&>span:last-child]:hidden" />
      )}
      <div className="flex shrink-0 items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
        {viewable && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={props.onPreview} aria-label="View in 3D">
                <Box className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View in 3D</TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={props.onDownload}
              aria-label="Download"
              disabled={!file.current_version}
            >
              <Download className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Download</TooltipContent>
        </Tooltip>
        {props.editable && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={props.isMe ? props.onNewVersion : props.onCheckOut}
                aria-label={props.isMe ? 'Check in new version' : 'Download to edit'}
                disabled={!file.current_version || (checkedOut && !props.isMe)}
              >
                {props.isMe ? <FileUp className="h-4 w-4" /> : <FileDown className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {props.isMe
                ? 'Check in new version'
                : checkedOut
                  ? 'Someone else has this checked out'
                  : 'Download to edit (checks the file out to you)'}
            </TooltipContent>
          </Tooltip>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={`Actions for ${file.name}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 text-xs">
            <DropdownMenuItem onSelect={props.onOpen}>
              <FileClock className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
              Details & version history
            </DropdownMenuItem>
            {viewable && (
              <DropdownMenuItem onSelect={props.onPreview}>
                <Box className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                View in 3D
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={props.onDownload} disabled={!file.current_version}>
              <Download className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
              Download
            </DropdownMenuItem>
            {props.editable && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={props.onCheckOut} disabled={!file.current_version || (checkedOut && !props.isMe)}>
                  <FileDown className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  Download to edit
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={props.onNewVersion}>
                  <FileUp className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  Upload new version
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={props.onRename}>
                  <Pencil className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Folder className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                    Move to…
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-72 w-56 overflow-y-auto text-xs">
                    <DropdownMenuLabel>Move to</DropdownMenuLabel>
                    <DropdownMenuItem disabled={file.folder_id === null} onSelect={() => props.onMove(null)}>
                      <HardDrive className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                      Drive root
                    </DropdownMenuItem>
                    {props.flatFolders.map((n) => (
                      <DropdownMenuItem
                        key={n.folder.id}
                        disabled={file.folder_id === n.folder.id}
                        onSelect={() => props.onMove(n.folder.id)}
                      >
                        <span aria-hidden style={{ width: n.depth * 12 }} className="shrink-0" />
                        <Folder className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate">{n.folder.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {isImageFile(file.name) && file.current_version && (
                  <DropdownMenuItem onSelect={props.onSetCover} disabled={props.isCover}>
                    <ImageIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                    {props.isCover ? 'Current cover image' : 'Use as project cover'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={props.onDelete}>
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Delete file
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Name dialog (new folder / rename)
// ---------------------------------------------------------------------------

function NameDialog({
  edit,
  onClose,
  onSubmit,
  busy,
}: {
  edit: NameEdit | null;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  busy: boolean;
}) {
  const [value, setValue] = useState('');
  useEffect(() => {
    if (!edit) setValue('');
    else if (edit.kind === 'rename-folder') setValue(edit.folder.name);
    else if (edit.kind === 'rename-file') setValue(edit.file.name);
    else setValue('');
  }, [edit]);

  const title =
    edit?.kind === 'new-folder' ? 'New folder' : edit?.kind === 'rename-folder' ? 'Rename folder' : 'Rename file';

  return (
    <Dialog open={!!edit} onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) void onSubmit(value.trim());
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="eng-name-input">Name</Label>
            <Input
              id="eng-name-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              placeholder={edit?.kind === 'new-folder' ? 'e.g. CAD, Drawings, Prints' : undefined}
              onFocus={(e) => {
                if (edit?.kind === 'rename-file') {
                  const dot = value.lastIndexOf('.');
                  e.currentTarget.setSelectionRange(0, dot > 0 ? dot : value.length);
                } else {
                  e.currentTarget.select();
                }
              }}
            />
            {edit?.kind === 'rename-file' && (
              <p className="text-xs text-muted-foreground">
                Keep the extension so the viewer and your CAD tool still recognise the format.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !value.trim()}>
              {busy ? 'Saving…' : edit?.kind === 'new-folder' ? 'Create folder' : 'Rename'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
